import { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { supabasePartner } from '../../lib/supabase';
import { labelBase, borderBase, inputBase } from '../../components/shared/helpers';
import ModalShell from '../../components/shared/ModalShell';

// FileReader → raw base64 (data: prefix stripped). The partner-self-upload edge
// function accepts either form, but we send the bare payload to be explicit.
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

// Inline status line under a section's Save button.
function Msg({ msg }) {
  if (!msg?.text) return null;
  return (
    <p
      className={`font-dm-sans text-[13px] font-medium mt-3 leading-relaxed ${msg.type === 'error' ? 'text-[#E5484D]' : 'text-[#C9A84C]'}`}
      role="status"
    >
      {msg.text}
    </p>
  );
}

// Shared field wrappers (same visual language as the partner agent step).
function TextField({ label, value, onChange, type = 'text', placeholder, inputMode, maxLength, autoComplete }) {
  return (
    <div className="group flex flex-col gap-2">
      <label className={labelBase}>{label}</label>
      <div className={borderBase}>
        <input
          type={type}
          value={value}
          onChange={onChange}
          className={inputBase}
          placeholder={placeholder}
          inputMode={inputMode}
          maxLength={maxLength}
          autoComplete={autoComplete}
        />
      </div>
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder }) {
  return (
    <div className="group flex flex-col gap-2">
      <label className={labelBase}>{label}</label>
      <div className={borderBase}>
        <textarea
          value={value}
          onChange={onChange}
          rows={2}
          className={`${inputBase} resize-none leading-relaxed`}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
}

// Signature portal card (gold gradient hairline → navy panel).
function SectionCard({ title, subtitle, children }) {
  return (
    <div className="p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)]">
      <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.4)] px-6 py-6 md:px-7 md:py-7">
        <h2 className="font-cormorant font-normal text-[1.6rem] leading-[1.1] tracking-[-0.01em] text-white mb-1">
          {title}
        </h2>
        {subtitle && (
          <p className="font-dm-sans text-sm text-white/55 font-light mb-5 leading-relaxed">{subtitle}</p>
        )}
        {!subtitle && <div className="mb-5" />}
        {children}
      </div>
    </div>
  );
}

const saveBtn = (busy) =>
  `mt-6 px-7 py-[13px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35] ${
    busy ? 'opacity-60 cursor-not-allowed' : 'active:scale-[0.97] hover:bg-[#B8973E] cursor-pointer'
  }`;

export default function PartnerProfile() {
  const { partnerSession, partnerProfile, loadingAuth, refreshProfile } = usePartnerAuth();
  const navigate = useNavigate();
  const partnerId = partnerProfile?.id ?? null;

  // ── Contact section ─────────────────────────────────────────────────────────
  const [contactPerson, setContactPerson] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [contactSaving, setContactSaving] = useState(false);
  const [contactMsg, setContactMsg] = useState({ type: '', text: '' });

  // ── GST section ─────────────────────────────────────────────────────────────
  const [gstin, setGstin] = useState('');
  const [gstLegalName, setGstLegalName] = useState('');
  const [gstAddress, setGstAddress] = useState('');
  const [gstState, setGstState] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [gstSaving, setGstSaving] = useState(false);
  const [gstMsg, setGstMsg] = useState({ type: '', text: '' });

  // GST certificate self-upload
  const [certOnFile, setCertOnFile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [certMsg, setCertMsg] = useState({ type: '', text: '' });

  // ── Agents section ──────────────────────────────────────────────────────────
  const [agents, setAgents] = useState(null); // null = loading
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [removingAgent, setRemovingAgent] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Seed the editable Contact + GST fields from the profile, once per identity.
  // Keyed on the partner id so a post-save refreshProfile() (same id) never
  // clobbers the values the partner just saved / is editing.
  useEffect(() => {
    if (!partnerProfile) return;
    setContactPerson(partnerProfile.contact_person || '');
    setPhone(partnerProfile.phone_number || '');
    setEmail(partnerProfile.email || '');
    setNotes(partnerProfile.notes || '');
    setGstin(partnerProfile.gstin || '');
    setGstLegalName(partnerProfile.gst_legal_name || '');
    setGstAddress(partnerProfile.gst_address || '');
    setGstState(partnerProfile.state || '');
    setStateCode(partnerProfile.state_code || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerProfile?.id]);

  // Is there already a GST certificate on file?
  useEffect(() => {
    if (!partnerId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabasePartner
        .from('partner_documents')
        .select('id')
        .eq('partner_id', partnerId)
        .eq('doc_type', 'gst_cert')
        .maybeSingle();
      if (!cancelled && data) setCertOnFile(true);
    })();
    return () => { cancelled = true; };
  }, [partnerId]);

  // Load active agents for this partner.
  useEffect(() => {
    if (!partnerId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabasePartner
        .from('partner_agents')
        .select('id, agent_name, agent_phone')
        .eq('partner_id', partnerId)
        .eq('is_active', true)
        .order('created_at', { ascending: true });
      if (cancelled) return;
      setAgents(!error && data ? data : []);
    })();
    return () => { cancelled = true; };
  }, [partnerId]);

  const emailValid = (v) => v.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const handleSaveContact = async () => {
    if (contactSaving) return;
    if (contactPerson.trim() === '') { setContactMsg({ type: 'error', text: 'Contact person is required.' }); return; }
    if (phone.replace(/\D/g, '').length < 10) { setContactMsg({ type: 'error', text: 'Enter a valid phone number (at least 10 digits).' }); return; }
    if (!emailValid(email)) { setContactMsg({ type: 'error', text: 'Enter a valid email address.' }); return; }
    setContactSaving(true);
    setContactMsg({ type: '', text: '' });
    const { error } = await supabasePartner
      .from('partners')
      .update({
        contact_person: contactPerson.trim(),
        phone_number: phone.trim(),
        email: email.trim(),
        notes: notes.trim(),
      })
      .eq('id', partnerId);
    if (error) {
      setContactMsg({ type: 'error', text: 'Could not save your contact details. Please try again.' });
      setContactSaving(false);
      return;
    }
    setContactMsg({ type: 'success', text: 'Contact details saved.' });
    if (refreshProfile) await refreshProfile();
    setContactSaving(false);
  };

  const handleSaveGst = async () => {
    if (gstSaving) return;
    setGstSaving(true);
    setGstMsg({ type: '', text: '' });
    const { error } = await supabasePartner
      .from('partners')
      .update({
        gstin: gstin.trim(),
        gst_legal_name: gstLegalName.trim(),
        gst_address: gstAddress.trim(),
        state: gstState.trim(),
        state_code: stateCode.trim(),
      })
      .eq('id', partnerId);
    if (error) {
      setGstMsg({ type: 'error', text: 'Could not save your GST details. Please try again.' });
      setGstSaving(false);
      return;
    }
    setGstMsg({ type: 'success', text: 'GST details saved.' });
    if (refreshProfile) await refreshProfile();
    setGstSaving(false);
  };

  const handleCertFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file) return;
    setCertMsg({ type: '', text: '' });

    const okTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!okTypes.includes(file.type)) { setCertMsg({ type: 'error', text: 'File must be a PDF, JPG, or PNG.' }); return; }
    if (file.size > 3 * 1024 * 1024) { setCertMsg({ type: 'error', text: 'File must be 3MB or smaller.' }); return; }

    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      // No partnerId is sent — the edge function derives the partner from the
      // caller's session JWT (approved-partner check enforced server-side).
      const { data, error } = await supabasePartner.functions.invoke('partner-self-upload', {
        body: { docType: 'gst_cert', file: { data: base64, filename: file.name, mimeType: file.type } },
      });
      if (error || (data && data.error)) {
        setCertMsg({ type: 'error', text: (data && data.error) || 'Could not upload the certificate. Please try again.' });
      } else {
        setCertOnFile(true);
        setCertMsg({ type: 'success', text: 'GST certificate uploaded.' });
      }
    } catch (err) {
      console.error('GST cert upload failed:', err);
      setCertMsg({ type: 'error', text: 'Could not upload the certificate. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  // ── Agents: add / edit / remove ─────────────────────────────────────────────
  const addValid = newName.trim().length > 0 && newPhone.trim().length >= 10;
  const editValid = editName.trim().length > 0 && editPhone.trim().length >= 10;

  const openEdit = (agent) => {
    setShowAdd(false);
    setAddError('');
    setEditingId(agent.id);
    setEditName(agent.agent_name || '');
    setEditPhone(agent.agent_phone || '');
    setEditError('');
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditPhone('');
    setEditError('');
  };

  const handleAdd = async () => {
    if (!addValid || saving) return;
    setSaving(true);
    setAddError('');
    const { data, error } = await supabasePartner
      .from('partner_agents')
      .insert({ partner_id: partnerId, agent_name: newName.trim(), agent_phone: newPhone.trim(), is_active: true })
      .select('id, agent_name, agent_phone')
      .single();
    if (error || !data) {
      setAddError('Could not save the agent. Please try again.');
      setSaving(false);
      return;
    }
    setAgents((prev) => [...(prev || []), data]);
    setNewName('');
    setNewPhone('');
    setShowAdd(false);
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (!editValid || editSaving || !editingId) return;
    setEditSaving(true);
    setEditError('');
    const { data, error } = await supabasePartner
      .from('partner_agents')
      .update({ agent_name: editName.trim(), agent_phone: editPhone.trim() })
      .eq('id', editingId)
      .select('id, agent_name, agent_phone')
      .single();
    if (error || !data) {
      setEditError('Could not update the agent. Please try again.');
      setEditSaving(false);
      return;
    }
    setAgents((prev) => (prev || []).map((a) => (a.id === data.id ? data : a)));
    cancelEdit();
    setEditSaving(false);
  };

  const handleRemove = async () => {
    if (!removingAgent || removing) return;
    setRemoving(true);
    const { error } = await supabasePartner
      .from('partner_agents')
      .update({ is_active: false })
      .eq('id', removingAgent.id);
    if (error) {
      setRemoving(false);
      setRemovingAgent(null);
      return;
    }
    setAgents((prev) => (prev || []).filter((a) => a.id !== removingAgent.id));
    if (editingId === removingAgent.id) cancelEdit();
    setRemoving(false);
    setRemovingAgent(null);
  };

  // Guards — wait for the auth check, then require an approved partner session.
  if (loadingAuth) {
    return (
      <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">
          <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed text-center">
            Loading...
          </p>
        </div>
      </div>
    );
  }
  if (!partnerSession) return <Navigate to="/partner/login" replace />;
  if (partnerProfile?.approval_status !== 'approved') return <Navigate to="/partner" replace />;

  const agentsLoading = agents === null;

  return (
    <div className="w-full max-w-[720px] mx-auto px-4 card-enter">

      {/* Header row: back to dashboard · heading */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate('/partner')}
          className="flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] shrink-0"
          aria-label="Back to dashboard"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div>
          <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)]">
            Partner Portal
          </p>
          <h1 className="font-cormorant font-normal text-[2rem] md:text-[2.4rem] leading-[1.05] tracking-[-0.01em] text-white">
            My Profile
          </h1>
        </div>
      </div>

      <div className="flex flex-col gap-5">

        {/* ── 1. Contact details ── */}
        <SectionCard title="Contact Details" subtitle="How we reach you about bookings and your account.">
          {/* Company name — LOCKED */}
          <div className="flex flex-col gap-2 mb-5">
            <span className={labelBase.replace('group-focus-within:text-[rgba(212,168,67,0.85)]', '')}>Company Name</span>
            <div className="flex items-center justify-between gap-4 border-b border-white/[0.13] pb-2.5">
              <span className="font-dm-sans text-[15px] font-light text-white/85">
                {partnerProfile?.name || '—'}
              </span>
              <span className="font-dm-sans text-[11px] uppercase tracking-[0.1em] text-white/35 shrink-0">Locked</span>
            </div>
            <p className="font-dm-sans text-[13px] font-light text-white/45 leading-relaxed">
              Contact us at 9854958549 / 9435558001 to change your company name.
            </p>
          </div>

          <div className="flex flex-col gap-5">
            <TextField label="Contact Person" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} placeholder="Full name" autoComplete="name" />
            <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} inputMode="numeric" maxLength={15} placeholder="Contact number" autoComplete="tel" />
            <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@example.com" autoComplete="email" />
            <TextArea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything we should know" />
          </div>

          <button type="button" onClick={handleSaveContact} disabled={contactSaving} className={saveBtn(contactSaving)}>
            {contactSaving ? 'Saving...' : 'Save Contact Details'}
          </button>
          <Msg msg={contactMsg} />
        </SectionCard>

        {/* ── 2. GST details ── */}
        <SectionCard title="GST Details" subtitle="Used on your invoices. Keep this up to date.">
          <div className="flex flex-col gap-5">
            <TextField label="GSTIN" value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} maxLength={15} placeholder="15-character GSTIN" />
            {gstin.trim() !== '' && gstin.trim().length !== 15 && (
              <p className="font-dm-sans text-[13px] font-light text-white/45 leading-relaxed -mt-3">
                A GSTIN is usually 15 characters.
              </p>
            )}
            <TextField label="Legal Name" value={gstLegalName} onChange={(e) => setGstLegalName(e.target.value)} placeholder="Registered legal name" />
            <TextArea label="GST Address" value={gstAddress} onChange={(e) => setGstAddress(e.target.value)} placeholder="Registered address" />
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex-1">
                <TextField label="State" value={gstState} onChange={(e) => setGstState(e.target.value)} placeholder="e.g. Assam" />
              </div>
              <div className="sm:w-[140px]">
                <TextField label="State Code" value={stateCode} onChange={(e) => setStateCode(e.target.value.replace(/\D/g, ''))} inputMode="numeric" maxLength={2} placeholder="e.g. 18" />
              </div>
            </div>
          </div>

          <button type="button" onClick={handleSaveGst} disabled={gstSaving} className={saveBtn(gstSaving)}>
            {gstSaving ? 'Saving...' : 'Save GST Details'}
          </button>
          <Msg msg={gstMsg} />

          {/* GST certificate upload */}
          <div className="mt-6 pt-6 border-t border-white/[0.08]">
            <div className="flex items-center justify-between gap-4 mb-2">
              <span className="font-dm-sans text-[13px] uppercase tracking-[0.14em] font-medium text-white/60">
                GST Certificate
              </span>
              {certOnFile && (
                <span className="font-dm-sans text-[13px] font-medium text-[#C9A84C]">
                  On file &#10003;
                </span>
              )}
            </div>
            <p className="font-dm-sans text-[13px] font-light text-white/45 leading-relaxed mb-3">
              Upload a PDF, JPG, or PNG (max 3MB). Uploading again replaces the current file.
            </p>
            <label
              className={`inline-flex items-center justify-center px-5 py-[11px] rounded-full border border-[rgba(212,168,67,0.4)] font-dm-sans font-medium text-[12.5px] tracking-[0.12em] uppercase transition-[border-color,background-color,opacity] duration-200 ${
                uploading
                  ? 'opacity-60 cursor-not-allowed text-white/50'
                  : 'text-[#D4A843] hover:border-[rgba(212,168,67,0.7)] hover:bg-[rgba(212,168,67,0.06)] cursor-pointer'
              }`}
            >
              {uploading ? 'Uploading...' : certOnFile ? 'Replace Certificate' : 'Upload Certificate'}
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={handleCertFile}
                disabled={uploading}
                className="hidden"
              />
            </label>
            <Msg msg={certMsg} />
          </div>
        </SectionCard>

        {/* ── 3. Agents ── */}
        <SectionCard title="Agents" subtitle="Contacts a booking can be attributed to. Tickets are sent to the selected agent's WhatsApp.">
          {agentsLoading ? (
            <p className="font-dm-sans text-sm text-white/55 font-light py-4 leading-relaxed">Loading agents...</p>
          ) : agents.length === 0 ? (
            <p className="font-dm-sans text-sm text-white/60 font-light leading-relaxed mb-4">
              No agents yet. Add one below.
            </p>
          ) : (
            <div className="flex flex-col gap-1 sm:gap-2 mb-3">
              {agents.map((agent) => (
                <div
                  key={agent.id}
                  className="rounded-xl border border-white/10 bg-[#162840] transition-[border-color] duration-200"
                >
                  <div className="flex items-center">
                    <div className="flex-1 flex flex-col gap-0.5 min-w-0 px-4 py-3">
                      <span className="font-dm-sans text-[15px] font-light text-white truncate">{agent.agent_name}</span>
                      <span className="font-dm-sans text-[13px] font-light text-white/55">{agent.agent_phone}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => (editingId === agent.id ? cancelEdit() : openEdit(agent))}
                      className="shrink-0 self-stretch px-3.5 font-dm-sans text-[12px] font-medium text-[#C9A84C] hover:text-[#B8973E] transition-colors duration-200 focus-visible:outline-none"
                      aria-label={`Edit ${agent.agent_name}`}
                    >
                      {editingId === agent.id ? 'Close' : 'Edit'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRemovingAgent(agent)}
                      className="shrink-0 self-stretch pr-4 pl-1.5 font-dm-sans text-[12px] font-medium text-white/45 hover:text-[#E5484D] transition-colors duration-200 focus-visible:outline-none rounded-r-xl"
                      aria-label={`Remove ${agent.agent_name}`}
                    >
                      Remove
                    </button>
                  </div>

                  {editingId === agent.id && (
                    <div className="px-4 pb-4 pt-3 mt-1 border-t border-white/[0.08] flex flex-col gap-4">
                      <div className="group flex flex-col gap-2">
                        <label className={labelBase}>Agent Name</label>
                        <div className={borderBase}>
                          <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className={inputBase} autoComplete="name" placeholder="Full name" />
                        </div>
                      </div>
                      <div className="group flex flex-col gap-2">
                        <label className={labelBase}>Phone Number</label>
                        <div className={borderBase}>
                          <input type="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value.replace(/\D/g, ''))} className={inputBase} autoComplete="tel" inputMode="numeric" maxLength={10} placeholder="10-digit WhatsApp number" />
                        </div>
                      </div>
                      {editError && (
                        <p className="font-dm-sans text-[13px] font-light text-[#E5484D] leading-relaxed">{editError}</p>
                      )}
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={handleUpdate}
                          disabled={!editValid || editSaving}
                          className={`flex-1 py-[12px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12px] tracking-[0.12em] uppercase rounded-full transition-[transform,opacity,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none ${
                            editValid && !editSaving ? 'opacity-100 active:scale-[0.97] hover:bg-[#B8973E] cursor-pointer' : 'opacity-30 cursor-not-allowed'
                          }`}
                        >
                          {editSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={editSaving}
                          className="flex-1 py-[12px] rounded-full border border-white/[0.18] text-white/70 font-dm-sans text-[12px] font-medium tracking-[0.12em] uppercase hover:border-white/30 hover:text-white/90 transition-[border-color,color] duration-200 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add agent */}
          {!agentsLoading && !showAdd && (
            <button
              type="button"
              onClick={() => { setShowAdd(true); setAddError(''); cancelEdit(); }}
              className="font-dm-sans text-[13px] font-medium text-[#C9A84C] hover:text-[#B8973E] transition-colors duration-200"
            >
              + Add agent
            </button>
          )}

          {!agentsLoading && showAdd && (
            <div className="rounded-xl border border-white/[0.1] bg-[#122236] px-4 py-4 flex flex-col gap-4">
              <div className="group flex flex-col gap-2">
                <label className={labelBase}>Agent Name</label>
                <div className={borderBase}>
                  <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} className={inputBase} autoComplete="name" placeholder="Full name" />
                </div>
              </div>
              <div className="group flex flex-col gap-2">
                <label className={labelBase}>Phone Number</label>
                <div className={borderBase}>
                  <input type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, ''))} className={inputBase} autoComplete="tel" inputMode="numeric" maxLength={10} placeholder="10-digit WhatsApp number" />
                </div>
              </div>
              {addError && (
                <p className="font-dm-sans text-[13px] font-light text-[#E5484D] leading-relaxed">{addError}</p>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={!addValid || saving}
                  className={`flex-1 py-[12px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12px] tracking-[0.12em] uppercase rounded-full transition-[transform,opacity,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none ${
                    addValid && !saving ? 'opacity-100 active:scale-[0.97] hover:bg-[#B8973E] cursor-pointer' : 'opacity-30 cursor-not-allowed'
                  }`}
                >
                  {saving ? 'Saving...' : 'Save Agent'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAdd(false); setNewName(''); setNewPhone(''); setAddError(''); }}
                  disabled={saving}
                  className="flex-1 py-[12px] rounded-full border border-white/[0.18] text-white/70 font-dm-sans text-[12px] font-medium tracking-[0.12em] uppercase hover:border-white/30 hover:text-white/90 transition-[border-color,color] duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </SectionCard>

      </div>

      {/* Remove-agent confirm */}
      {removingAgent && (
        <ModalShell
          ariaLabel="Remove agent"
          onClose={removing ? undefined : () => setRemovingAgent(null)}
        >
          <div className="flex items-center gap-3 mb-4 w-full max-w-[180px]">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[rgba(212,168,67,0.4)]" />
            <div className="w-1.5 h-1.5 rotate-45 bg-[rgba(212,168,67,0.55)] shrink-0" />
            <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[rgba(212,168,67,0.4)]" />
          </div>
          <h2 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-3">
            Remove {removingAgent.agent_name}?
          </h2>
          <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-6">
            This agent will no longer be available for new bookings. Existing bookings are unaffected.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleRemove}
              disabled={removing}
              className={`flex-1 py-[13px] bg-transparent border border-[rgba(229,72,77,0.5)] text-[#E5484D] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,border-color,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none ${
                removing ? 'opacity-60 cursor-not-allowed' : 'active:scale-[0.97] hover:bg-[rgba(229,72,77,0.08)] hover:border-[rgba(229,72,77,0.8)]'
              }`}
            >
              {removing ? 'Removing...' : 'Remove'}
            </button>
            <button
              type="button"
              onClick={() => { if (!removing) setRemovingAgent(null); }}
              disabled={removing}
              className="flex-1 py-[13px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] hover:bg-[#B8973E] focus-visible:outline-none disabled:opacity-50"
            >
              Keep
            </button>
          </div>
        </ModalShell>
      )}

    </div>
  );
}
